import { useState, useCallback, useMemo, useRef, useEffect, KeyboardEvent } from 'react';
import type { Hymn } from '../types';

interface HymnTypeaheadProps {
  hymns: Hymn[];
  value: string;
  displayValue: string;
  onChange: (hymnId: string) => void;
  sacramentOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function HymnTypeahead({
  hymns,
  value,
  displayValue,
  onChange,
  sacramentOnly = false,
  placeholder = 'Search hymn...',
  disabled = false,
}: HymnTypeaheadProps) {
  const [inputValue, setInputValue] = useState(displayValue);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  const filteredHymns = useMemo(() => {
    const list = sacramentOnly ? hymns.filter((h) => h.isSacramentHymn) : hymns;
    if (!inputValue) return list;
    const query = inputValue.toLowerCase();
    return list.filter(
      (h) =>
        String(h.number).includes(inputValue) ||
        h.title.toLowerCase().includes(query)
    );
  }, [hymns, inputValue, sacramentOnly]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  }, []);

  const handleFocus = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
      if (!value) {
        setInputValue('');
      } else {
        setInputValue(displayValue);
      }
    }, 200);
  }, [value, displayValue]);

  const selectHymn = useCallback(
    (hymn: Hymn) => {
      setInputValue(`${hymn.number} - ${hymn.title}`);
      onChange(String(hymn.number));
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        setIsOpen(true);
        e.preventDefault();
        return;
      }

      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredHymns.length - 1)
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredHymns[highlightedIndex]) {
            selectHymn(filteredHymns[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    },
    [isOpen, filteredHymns, highlightedIndex, selectHymn]
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedEl = dropdownRef.current.querySelector(
        '.hymn-typeahead__item--highlighted'
      );
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className="hymn-typeahead">
      <input
        ref={inputRef}
        type="text"
        className="hymn-typeahead__input"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
      />
      <div
        ref={dropdownRef}
        className={`hymn-typeahead__dropdown ${
          isOpen ? 'hymn-typeahead__dropdown--show' : ''
        }`}
      >
        {filteredHymns.length === 0 ? (
          <div className="hymn-typeahead__empty">No hymns found</div>
        ) : (
          filteredHymns.map((hymn, index) => (
            <div
              key={hymn.number}
              className={`hymn-typeahead__item ${
                index === highlightedIndex
                  ? 'hymn-typeahead__item--highlighted'
                  : ''
              }`}
              onMouseDown={() => selectHymn(hymn)}
            >
              <span className="hymn-typeahead__number">{hymn.number}</span> -{' '}
              {hymn.title}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
